import React, { useState, useEffect, useCallback } from "react";
import { X, Cloud, Smile, Frown, Meh, Search, Filter } from "lucide-react";
import { useTheme } from "../contexts/ThemeContext";
import { useAuth } from "../contexts/AuthContext";
import api from "../services/api";

const COLORS = ['#f97316','#ef4444','#8b5cf6','#3b82f6','#10b981','#f59e0b','#ec4899','#06b6d4','#84cc16','#6366f1'];
const SENT_ICON = { positive: Smile, negative: Frown, neutral: Meh };
const SENT_COLOR = { positive: 'text-green-500', negative: 'text-red-500', neutral: 'text-gray-400' };
const SENT_BG = { positive: 'bg-green-500/10 border-green-500/30', negative: 'bg-red-500/10 border-red-500/30', neutral: '' };
const SENT_LABEL = { positive: 'Positiva', negative: 'Negativa', neutral: 'Neutra' };

const GroupWordCloud = ({ groupId, onClose }) => {
  const { darkMode } = useTheme();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [days, setDays] = useState(30);
  const [loading, setLoading] = useState(true);
  const [filterSentiment, setFilterSentiment] = useState('all');
  const [filterWord, setFilterWord] = useState('');
  const [activeWord, setActiveWord] = useState('');

  const load = useCallback(() => {
    if (!groupId) return;
    setLoading(true);
    const params = { cliente_id: user.cliente_id, days };
    if (filterSentiment !== 'all') params.sentiment = filterSentiment;
    if (activeWord) params.word = activeWord;
    api.get(`/grupos-whatsapp/${groupId}/wordcloud`, { params })
      .then(r => setData(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [groupId, days, filterSentiment, activeWord, user.cliente_id]);

  useEffect(() => { load(); }, [load]);

  if (!groupId) return null;

  const bg = darkMode ? 'bg-zinc-800' : 'bg-white';
  const border = darkMode ? 'border-zinc-700' : 'border-gray-200';
  const text = darkMode ? 'text-gray-200' : 'text-gray-800';
  const textMuted = darkMode ? 'text-gray-400' : 'text-gray-500';
  const maxCount = data?.words?.[0]?.count || 1;

  const handleWordClick = (word) => {
    setActiveWord(prev => prev === word ? '' : word);
  };

  const handleSentimentFilter = (s) => {
    setFilterSentiment(prev => prev === s ? 'all' : s);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={onClose}>
      <div className={`${bg} rounded-xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto m-4`} onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className={`flex items-center justify-between p-4 border-b ${border}`}>
          <div className="flex items-center gap-2">
            <Cloud className="h-5 w-5 text-nomoz-orange" />
            <h2 className={`text-lg font-bold ${text}`}>Palavras & Sentimento</h2>
          </div>
          <div className="flex items-center gap-2">
            <select value={days} onChange={e => setDays(+e.target.value)}
              className={`text-sm rounded px-2 py-1 border ${darkMode ? 'bg-zinc-700 border-zinc-600 text-white' : 'bg-white border-gray-300'}`}>
              <option value={7}>7 dias</option>
              <option value={14}>14 dias</option>
              <option value={30}>30 dias</option>
              <option value={90}>90 dias</option>
            </select>
            <button onClick={onClose} className="p-1 rounded hover:bg-opacity-20 hover:bg-gray-500"><X className="h-5 w-5" /></button>
          </div>
        </div>

        {loading && !data ? (
          <div className="p-8 text-center"><div className="animate-spin h-8 w-8 border-2 border-nomoz-orange border-t-transparent rounded-full mx-auto" /></div>
        ) : !data ? (
          <div className={`p-8 text-center ${textMuted}`}>Sem dados</div>
        ) : (
          <div className="p-4 space-y-4">
            {/* Sentiment bar + clickable filters */}
            <div className={`p-3 rounded-lg border ${border}`}>
              <div className="flex items-center justify-between mb-2">
                <span className={`text-xs ${textMuted}`}>{data.total_messages} mensagens — clique pra filtrar:</span>
                {filterSentiment !== 'all' && (
                  <button onClick={() => setFilterSentiment('all')} className="text-xs text-nomoz-orange hover:underline">Limpar filtro</button>
                )}
              </div>
              <div className="flex gap-2">
                {['positive', 'negative', 'neutral'].map(s => {
                  const Icon = SENT_ICON[s];
                  const active = filterSentiment === s;
                  return (
                    <button key={s} onClick={() => handleSentimentFilter(s)}
                      className={`flex-1 flex items-center justify-center gap-1.5 p-2 rounded-lg border transition-all ${
                        active ? SENT_BG[s] + ' border-current ring-1 ring-current' : `${darkMode ? 'border-zinc-600 hover:bg-zinc-700' : 'border-gray-200 hover:bg-gray-50'}`
                      }`}>
                      <Icon className={`h-4 w-4 ${SENT_COLOR[s]}`} />
                      <span className={`text-sm font-bold ${SENT_COLOR[s]}`}>{data.sentiment[s + '_pct']}%</span>
                      <span className={`text-xs ${textMuted}`}>({data.sentiment[s]})</span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Active filters */}
            {(activeWord || filterSentiment !== 'all') && (
              <div className="flex items-center gap-2 flex-wrap">
                <Filter className={`h-3.5 w-3.5 ${textMuted}`} />
                {filterSentiment !== 'all' && (
                  <span className={`text-xs px-2 py-1 rounded-full border ${SENT_BG[filterSentiment]}`}>
                    {SENT_LABEL[filterSentiment]} <button onClick={() => setFilterSentiment('all')} className="ml-1">×</button>
                  </span>
                )}
                {activeWord && (
                  <span className="text-xs px-2 py-1 rounded-full bg-nomoz-orange/10 border border-nomoz-orange/30 text-nomoz-orange">
                    "{activeWord}" <button onClick={() => setActiveWord('')} className="ml-1">×</button>
                  </span>
                )}
                {loading && <div className="animate-spin h-3 w-3 border border-nomoz-orange border-t-transparent rounded-full" />}
              </div>
            )}

            {/* Word Cloud */}
            <div>
              <h3 className={`text-xs font-semibold mb-2 ${textMuted}`}>Clique numa palavra pra ver as mensagens:</h3>
              <div className="flex flex-wrap gap-1.5 justify-center p-3">
                {data.words.map((w, i) => {
                  const ratio = w.count / maxCount;
                  const size = 12 + ratio * 28;
                  const isActive = activeWord === w.word;
                  return (
                    <button key={w.word} onClick={() => handleWordClick(w.word)}
                      title={`${w.word}: ${w.count}x`}
                      className={`font-semibold transition-all hover:scale-110 ${isActive ? 'underline decoration-2' : 'hover:opacity-80'}`}
                      style={{ fontSize: size, color: isActive ? '#f97316' : COLORS[i % COLORS.length], opacity: isActive ? 1 : 0.5 + ratio * 0.5 }}>
                      {w.word}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Messages list */}
            {data.messages && data.messages.length > 0 && (
              <div>
                <h3 className={`text-xs font-semibold mb-2 ${textMuted}`}>
                  {data.messages.length >= 100 ? '100+ mensagens' : `${data.messages.length} mensagens`}
                  {filterSentiment !== 'all' && ` ${SENT_LABEL[filterSentiment].toLowerCase()}s`}
                  {activeWord && ` com "${activeWord}"`}
                </h3>
                <div className={`rounded-lg border ${border} divide-y ${darkMode ? 'divide-zinc-700' : 'divide-gray-100'} max-h-64 overflow-y-auto`}>
                  {data.messages.map((m, i) => {
                    const Icon = SENT_ICON[m.sentiment];
                    return (
                      <div key={i} className={`p-2.5 flex gap-2 ${darkMode ? 'hover:bg-zinc-700/50' : 'hover:bg-gray-50'}`}>
                        <Icon className={`h-4 w-4 mt-0.5 flex-shrink-0 ${SENT_COLOR[m.sentiment]}`} />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className={`text-xs font-semibold ${text}`}>{m.sender || m.phone}</span>
                            <span className={`text-[10px] ${textMuted}`}>{new Date(m.date).toLocaleString('pt-BR', { day:'2-digit', month:'2-digit', hour:'2-digit', minute:'2-digit' })}</span>
                          </div>
                          <p className={`text-sm ${text} break-words`}>{m.msg}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default GroupWordCloud;
